import { useSelector } from 'react-redux';

import { Appliance } from 'clipsal-cortex-types/src/api/api-appliances';
import { ImportLimitedDevice, UpdateImportLimitedDevice } from 'clipsal-cortex-types/src/api/api-import-limited-device';
import { ViewportType } from 'clipsal-cortex-utils/src/calculations/viewport-helpers';
import { useViewportType } from 'clipsal-cortex-utils/src/hooks/use-viewport-type';

import { baseApi } from '../../app/services/baseApi';
import { RootState } from '../../app/store';
import manualAddressSiteDesktopLargeImage from '../../assets/images/manual_address_image_desktop_lg.svg';
import manualAddressSiteDesktopImage from '../../assets/images/manual_address_image_desktop.svg';
import manualAddressSiteMobileImage from '../../assets/images/manual_address_image_mobile.svg';
import manualAddressSiteTabletImage from '../../assets/images/manual_address_image_tablet.svg';
import { selectSite } from '../site/siteSlice';

export const profileApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getNearmapImage: build.query<string, { size: ViewportType; siteID: number }>({
      query: ({ size, siteID }) => `/v1/sites/${siteID}/image_url?size=${size}`,
      providesTags: (_, __, { siteID }) => [{ type: 'NearmapImage', id: siteID }],
      transformResponse: async (data: { url: string }, _, { size }) => {
        const url = data.url;

        // check if url returns a valid image
        // if its invalid, then it means its a manual address site
        const imageUrl = await new Promise<string>((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve(data.url);
          img.onerror = () => {
            let newSiteImage = manualAddressSiteDesktopLargeImage;
            if (size === 'mobile') newSiteImage = manualAddressSiteMobileImage;
            if (size === 'tablet') newSiteImage = manualAddressSiteTabletImage;
            if (size === 'desktop') newSiteImage = manualAddressSiteDesktopImage;
            resolve(newSiteImage);
          };
        });

        return imageUrl;
      },
    }),
    getImportLimitedDevices: build.query<ImportLimitedDevice[], number>({
      query: (siteId) => `/v1/sites/${siteId}/import_limited_devices`,
      providesTags: ['ImportLimitedDevices'],
    }),
    updateImportLimitedDevice: build.mutation<ImportLimitedDevice, { id: number; body: UpdateImportLimitedDevice }>({
      query: ({ id, body }) => ({
        url: `/v1/import_limited_devices/${id}`,
        method: 'PATCH',
        body,
      }),
      onQueryStarted: async ({ id, body }, { dispatch, queryFulfilled, getState }) => {
        // Optimistic update so the user gets fast feedback. The mutation happens in the background.
        const state = getState();
        const siteId = (state as RootState).site.site_id;
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getImportLimitedDevices', siteId, (draft) => {
            // Update this setting in the drafted state. Note that immer provides mutability here
            const importLimitedDevice = draft.find((device) => device.id === id);
            if (importLimitedDevice) {
              importLimitedDevice.ignore_until_datetime_utc = body.ignore_until_datetime_utc;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['ImportLimitedDevices'],
    }),
    getSiteAppliances: build.query<Appliance[], number>({
      query: (siteID) => `/v1/sites/${siteID}/appliances`,
      providesTags: (_, __, siteId) => [{ type: 'Appliances', siteId }],
    }),
  }),
});

export const {
  useGetNearmapImageQuery,
  useGetImportLimitedDevicesQuery,
  useUpdateImportLimitedDeviceMutation,
  useGetSiteAppliancesQuery,
} = profileApi;

export const useNearmapImage = () => {
  const site = useSelector(selectSite);
  const { viewportType } = useViewportType();
  const { data } = useGetNearmapImageQuery({ size: viewportType, siteID: site.site_id });
  return data || '';
};

export function useImportLimitedDevices() {
  const site = useSelector(selectSite);
  const { data, ...rest } = useGetImportLimitedDevicesQuery(site.site_id);

  return { data: data ?? [], ...rest };
}

export const useSiteAppliances = () => {
  const site = useSelector(selectSite);
  const results = useGetSiteAppliancesQuery(site.site_id);
  return { ...results, data: results.data ?? [] };
};
